import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import Section from '../layout/Section';
import siteContent from '../../data/siteContent.json';

const Gallery = () => {
    const { gallery } = siteContent;
    const [activeCategory, setActiveCategory] = useState('all');
    const [selectedImage, setSelectedImage] = useState(null);
    const getSrcSet = useCallback((src) => {
        if (!src || !src.endsWith('.webp')) return undefined;
        const base = src.replace('.webp', '');
        return `${base}-480.webp 480w, ${base}-960.webp 960w, ${src} 1200w`;
    }, []);

    // Derive categories from the items present in the data for dynamic filtering, but use the labels from JSON
    const uniqueCategories = useMemo(() => {
        const cats = new Set(gallery.items.map(item => item.category));
        return ['all', ...Array.from(cats)];
    }, [gallery.items]);

    const filteredItems = useMemo(() => activeCategory === 'all'
        ? gallery.items.filter(item => item.isCompleted)
        : gallery.items.filter(item => item.category === activeCategory && item.isCompleted), [activeCategory, gallery.items]);

    // Navigation Logic
    const handleNavigate = useCallback((direction) => {
        if (!selectedImage) return;
        const currentIndex = filteredItems.findIndex(item => item.id === selectedImage.id);
        if (currentIndex === -1) return;

        let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

        // Loop navigation
        if (newIndex >= filteredItems.length) newIndex = 0;
        if (newIndex < 0) newIndex = filteredItems.length - 1;

        setSelectedImage(filteredItems[newIndex]);
    }, [selectedImage, filteredItems]);

    // Handle Keys
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedImage) return;

            if (e.key === 'Escape') {
                setSelectedImage(null);
            } else if (e.key === 'ArrowRight') {
                handleNavigate('next');
            } else if (e.key === 'ArrowLeft') {
                handleNavigate('prev');
            }
        };

        if (selectedImage) {
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
            // Ensure cleanup
            document.body.style.overflow = 'unset';
        };
    }, [selectedImage, handleNavigate]);

    return (
        <Section id="gallery" background="cream" className="py-16 md:py-32">
            {/* Header & KPI */}
            <div className="text-center max-w-4xl mx-auto mb-8 md:mb-12">
                <span className="text-primary-walnut font-bold tracking-widest uppercase text-sm mb-2 block">
                    {gallery.subtitle}
                </span>
                <h2 className="text-2xl md:text-5xl font-heading font-medium text-primary-charcoal mb-4 md:mb-6">
                    {gallery.title}
                </h2>
                <div className="flex md:flex-wrap items-center justify-center gap-2 md:gap-4 mb-6 md:mb-8 overflow-x-auto md:overflow-visible px-2 md:px-0">
                    {gallery.kpi.map((kpi, index) => (
                        <div key={index} className="flex items-center gap-2 bg-white px-3 py-1.5 md:px-4 md:py-2 rounded-full shadow-sm border border-gray-100 whitespace-nowrap">
                            <CheckCircle2 size={14} className="text-primary-walnut" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">{kpi}</span>
                        </div>
                    ))}
                </div>
                <p className="text-gray-500 leading-relaxed max-w-2xl mx-auto text-sm md:text-base">
                    {gallery.description}
                </p>
            </div>

            {/* Filter Buttons */}
            <div className="flex md:flex-wrap justify-start md:justify-center gap-2 md:gap-4 mb-8 md:mb-12 overflow-x-auto md:overflow-visible px-2 md:px-0">
                {uniqueCategories.map((catKey) => (
                    <button
                        key={catKey}
                        onClick={() => setActiveCategory(catKey)}
                        className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-all duration-300 transform hover:scale-105 whitespace-nowrap
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-wood/50
              ${activeCategory === catKey
                                ? 'bg-primary-charcoal text-white shadow-lg ring-2 ring-primary-charcoal ring-offset-2'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}
                    >
                        {gallery.categories[catKey] || catKey.toUpperCase()}
                    </button>
                ))}
            </div>

            {/* Grid */}
            <motion.div
                layout
                className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6"
            >
                <AnimatePresence mode="popLayout">
                    {filteredItems.map((item) => (
                        <motion.div
                            layout
                            initial={{ opacity: 0, scale: 0.95, filter: 'blur(4px)' }}
                            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, scale: 0.95, filter: 'blur(4px)' }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            key={item.id}
                            className="relative aspect-[4/3] group cursor-pointer overflow-hidden rounded-xl shadow-md bg-gray-100 outline-none focus:ring-4 focus:ring-primary-wood/50 transition-shadow"
                            onClick={() => setSelectedImage(item)}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedImage(item)}
                            tabIndex={0}
                            role="button"
                            aria-label={`${item.title} projesini incele`}
                        >
                            <img
                                src={item.cover}
                                srcSet={getSrcSet(item.cover)}
                                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                                alt={item.title}
                                loading="lazy"
                                decoding="async"
                                width="1200"
                                height="900"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 group-focus:scale-105"
                            />
                            {/* Premium Overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-all duration-500 ease-out flex flex-col items-center justify-center backdrop-blur-[1px]">
                                <div className="transform translate-y-4 group-hover:translate-y-0 group-focus:translate-y-0 transition-transform duration-500 ease-out flex flex-col items-center gap-2">
                                    <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-primary-charcoal shadow-lg mb-1 opacity-0 group-hover:opacity-100 transition-all duration-300 delay-100 transform scale-75 group-hover:scale-100">
                                        <ZoomIn size={20} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="text-white font-heading font-medium text-lg tracking-wide drop-shadow-md px-4 text-center opacity-0 group-hover:opacity-100 transition-all duration-300 delay-150 transform translate-y-2 group-hover:translate-y-0">
                                        {item.title}
                                    </h3>
                                    <span className="text-white/80 text-xs uppercase tracking-widest font-light opacity-0 group-hover:opacity-100 transition-all duration-300 delay-200">
                                        {item.year}
                                    </span>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </motion.div>

            {/* Lightbox Modal (Portal) */}
            {selectedImage && createPortal(
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        // Z-Index boosted to 9999 to cover EVERYTHING including fixed headers
                        className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm cursor-zoom-out"
                        onClick={() => setSelectedImage(null)}
                    >
                        {/* Static Elements Container */}
                        <div className="absolute inset-0 pointer-events-none">
                            {/* Close Button */}
                            <button
                                className="fixed top-6 right-6 z-[10000] text-white/80 hover:text-white bg-white/5 hover:bg-white/10 p-4 rounded-full transition-all backdrop-blur-md pointer-events-auto border border-white/10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedImage(null);
                                }}
                            >
                                <X size={24} strokeWidth={1.5} />
                            </button>

                            {/* Navigation Buttons */}
                            <button
                                className="fixed top-1/2 left-4 md:left-8 z-[10000] -translate-y-1/2 text-white/50 hover:text-white bg-transparent hover:bg-white/5 p-4 rounded-full transition-all pointer-events-auto border border-transparent hover:border-white/10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigate('prev');
                                }}
                            >
                                <ChevronLeft size={40} strokeWidth={1} />
                            </button>
                            <button
                                className="fixed top-1/2 right-4 md:right-8 z-[10000] -translate-y-1/2 text-white/50 hover:text-white bg-transparent hover:bg-white/5 p-4 rounded-full transition-all pointer-events-auto border border-transparent hover:border-white/10"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigate('next');
                                }}
                            >
                                <ChevronRight size={40} strokeWidth={1} />
                            </button>
                        </div>

                        {/* Image Container */}
                        <motion.div
                            key={selectedImage.id}
                            initial={{ scale: 0.98, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.98, opacity: 0 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="relative max-w-7xl w-full h-full flex flex-col items-center justify-center cursor-default pointer-events-auto py-8"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="relative inline-block max-h-full">
                                <img
                                    src={selectedImage.cover}
                                    alt={selectedImage.title}
                                    loading="eager"
                                    decoding="async"
                                    width="1600"
                                    height="1200"
                                    className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
                                />

                                {/* Watermark Logo - Anthracite/Dark Style */}
                                <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 opacity-80 z-20 pointer-events-none">
                                    <img
                                        src="/assets/logos/ERDE-256.png"
                                        srcSet="/assets/logos/ERDE-256.png 256w, /assets/logos/ERDE-512.png 512w"
                                        sizes="40px"
                                        alt="Erde Watermark"
                                        width="120"
                                        height="120"
                                        loading="lazy"
                                        decoding="async"
                                        className="h-6 md:h-10 w-auto object-contain brightness-0 opacity-70 drop-shadow-sm"
                                    />
                                </div>
                            </div>

                            <div className="mt-8 text-center z-20">
                                <h3 className="text-2xl font-heading font-light text-white tracking-widest">
                                    {selectedImage.title}
                                </h3>
                                <div className="flex items-center justify-center gap-3 mt-2 text-white/40 text-xs font-light tracking-[0.2em] uppercase">
                                    <span>{gallery.categories[selectedImage.category]}</span>
                                    <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                    <span>{selectedImage.year}</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </AnimatePresence>,
                document.body
            )}
        </Section>
    );
};

export default Gallery;
